/* eslint-disable import/no-anonymous-default-export */
import { Box, Typography } from "@material-ui/core";
import Icon from "@material-ui/icons/ShoppingBasket";
import React, { FC } from "react";
import {
  BooleanField,
  BooleanInput,
  Create,
  CreateButton,
  CreateProps,
  Datagrid,
  Edit,
  EditProps,
  Filter,
  FunctionField,
  List,
  ListActionsProps,
  ListProps,
  SaveButton,
  SelectInput,
  Show,
  ShowProps,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useListContext,
} from "react-admin";
import CrudBreadcrumb from "../components/CrudBreadcrumb";
import StatusPopover from "../components/datagrid/StatusPopover";
import BusinessUnitInput from "../components/inputs/BusinessUnitInput";
import ProductTypeInput from "../components/inputs/ProductTypeInput";
import StatusField from "../components/StatusField";

const isSR = process.env.REACT_APP_COMPANY_CODE === "SNR";

const productTypeChoices = isSR
  ? [
      { id: "instrument", name: "Device" },
      { id: "implant", name: "Consumable" },
      { id: "other", name: "Others" },
    ]
  : [
      { id: "instrument", name: "Instrument" },
      { id: "implant", name: "Implant" },
      { id: "other", name: "Others" },
    ];

const PRODUCT_TYPE_OPTIONS = productTypeChoices.map((c) => ({
  id: c.id,
  label: c.name,
}));

const productTypeMap = productTypeChoices.reduce<Record<string, string>>(
  (acc, choice) => {
    acc[choice.id] = choice.name;
    return acc;
  },
  {},
);

const getProductTypeLabel = (type: string): string => {
  return type
    .split(",")
    .map((t) => productTypeMap[t] ?? t)
    .join(", ");
};

const ProductDatagridTitle: FC = () => {
  const { filterValues } = useListContext();
  const type = filterValues?.type;

  if (!type) return null;

  return (
    <Box
      display="flex"
      alignItems="center"
      gridGap={26}
      style={{
        padding: "8px 16px 7px 16px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
      }}
    >
      <Typography variant="subtitle2" color="textPrimary">
        Product Type: {getProductTypeLabel(type)}
      </Typography>
    </Box>
  );
};

const ProductListActions: FC<ListActionsProps> = (props) => {
  const { filters, ...rest } = props;
  const { resource, showFilter, displayedFilters, filterValues } = props;

  return (
    <TopToolbar {...rest}>
      <StatusPopover
        options={PRODUCT_TYPE_OPTIONS}
        filterKey="type"
        label="Type"
      />
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}
      <CreateButton />
    </TopToolbar>
  );
};

const ResourceList: FC<ListProps> = (props) => (
  <List
    {...props}
    filterDefaultValues={{ type: "instrument,implant" }}
    exporter={false}
    sort={{ field: "id", order: "desc" }}
    filters={<ListFilter />}
    bulkActionButtons={false}
    actions={<ProductListActions />}
  >
    <Box>
      <ProductDatagridTitle />
      <Datagrid rowClick="show">
        <TextField source="id" />
        <TextField source="name" />
        <TextField source="business_unit.name" label="Business Unit" />
        <FunctionField
          label="Type"
          render={(record: any) => productTypeMap[record?.type] ?? record?.type}
        />
        <BooleanField source="is_one_time_use" />
        <StatusField source="status" />
      </Datagrid>
    </Box>
  </List>
);

const ListFilter: FC<any> = (props) => (
  <Filter {...props}>
    <TextInput source="id" label="ID" />
    <TextInput source="search" label="Name and Description" />
    <BusinessUnitInput source="business_unit_id" />
  </Filter>
);

const ResourceShow: FC<ShowProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={props.id}
        mode="show"
      />
      <Show {...props}>
        <SimpleShowLayout>
          <TextField source="id" />
          <TextField source="name" />
          <TextField source="business_unit.name" label="Business Unit" />
          <FunctionField
            label="Type"
            render={(record: any) =>
              productTypeMap[record?.type] ?? record?.type
            }
          />
          <TextField source="description" />
          <BooleanField source="is_one_time_use" />
          <StatusField source="status" />
        </SimpleShowLayout>
      </Show>
    </>
  );
};

const ResourceEdit: FC<EditProps> = (props) => {
  const IssueEditToolbar: FC<any> = (props) => (
    <Toolbar {...props}>
      <SaveButton disabled={props.pristine} />
    </Toolbar>
  );
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={props.id}
        mode="edit"
      />
      <Edit {...props}>
        <SimpleForm redirect="show" toolbar={<IssueEditToolbar />}>
          <TextField source="id" />
          <TextInput source="name" />
          <BusinessUnitInput source="business_unit_id" />
          <ProductTypeInput source="type" />
          <TextInput source="description" />
          <BooleanInput source="is_one_time_use" label="One Time Use" />
          <SelectInput
            source="status"
            choices={[
              { id: "active", name: "Active" },
              { id: "inactive", name: "Inactive" },
            ]}
          />
        </SimpleForm>
      </Edit>
    </>
  );
};

const ResourceCreate: FC<CreateProps> = (props) => {
  return (
    <>
      <CrudBreadcrumb
        resourceName={Common.resourceName}
        resourceId={""}
        mode="create"
      />
      <Create {...props}>
        <SimpleForm>
          <TextInput source="name" />
          <BusinessUnitInput source="business_unit_id" />
          <SelectInput source="type" choices={productTypeChoices} />
          <TextInput source="description" />
          <BooleanInput source="is_one_time_use" label="One Time Use" />
        </SimpleForm>
      </Create>
    </>
  );
};

const Common = {
  resourceName: "products",
};

export default {
  name: Common.resourceName,
  icon: Icon,
  list: ResourceList,
  show: ResourceShow,
  create: ResourceCreate,
  edit: ResourceEdit,
};
